import React from 'react';
import { Link } from 'react-router-dom';
import CmsImage from './CmsImage';

export default function ProductCard({ product, lang = 'da' }) {
  if (!product) return null;

  const isEn = lang === 'en';
  const getSlug = (entry) => {
    if (!entry) return '';
    const s = entry.slug;
    if (typeof s === 'string') return s;
    if (typeof s === 'object' && s !== null) {
      return s[lang] || s._ || s.da || Object.values(s)[0] || entry.id;
    }
    return entry.id || '';
  };

  const productSlug = getSlug(product);
  const detailUrl = isEn ? `/en/product/${productSlug}` : `/produkt/${productSlug}`;

  const mainImage = Array.isArray(product.billede) ? product.billede[0] : product.billede;

  // Resolve manufacturer display name
  const manufacturer =
    product.producent_tekst ||
    (Array.isArray(product.terms?.producent) ? product.terms.producent[0]?.toUpperCase() : '') ||
    '';

  return (
    <div className="group flex flex-col bg-white rounded-lg border border-stone-200 overflow-hidden shadow-sm hover:shadow-md hover:border-[#006368]/40 transition-all duration-200">
      {/* Product Image Container - uncropped, proper proportions */}
      <Link
        to={detailUrl}
        className="relative block w-full bg-white p-6 aspect-[4/3] flex items-center justify-center border-b border-stone-100 overflow-hidden"
      >
        {mainImage ? (
          <CmsImage
            src={mainImage}
            alt={product.titel || 'Produktbillede'}
            width={700}
            className="max-h-full max-w-full w-auto h-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-stone-400 bg-stone-50">
            <i className="fa-solid fa-industry text-4xl mb-2 text-stone-300" />
            <span className="text-xs">{isEn ? 'No image' : 'Billede mangler'}</span>
          </div>
        )}

        {/* Subtle hover prompt */}
        <div className="absolute inset-0 bg-[#006368]/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
      </Link>

      {/* Product Information */}
      <div className="p-5 flex-1 flex flex-col justify-between">
        <div>
          {/* Manufacturer & Item number row */}
          <div className="flex items-center justify-between gap-2 mb-2 text-xs">
            {manufacturer && (
              <span className="font-bold tracking-wider uppercase text-[#006368] bg-[#006368]/10 px-2 py-0.5 rounded">
                {manufacturer}
              </span>
            )}
            {product.varenummer && (
              <span className="text-stone-500 font-mono text-[11px]">
                {isEn ? 'Item no.' : 'Varenr.'}: {product.varenummer}
              </span>
            )}
          </div>

          {/* Product Title */}
          <h3 className="font-bold text-lg text-stone-900 group-hover:text-[#006368] transition-colors leading-snug">
            <Link to={detailUrl}>{product.titel}</Link>
          </h3>

          {/* Model / Serie */}
          {product.model && (
            <p className="text-xs font-semibold text-stone-600 mt-1">{product.model}</p>
          )}

          {/* Short description */}
          {product.kort_beskrivelse && (
            <p className="text-xs text-stone-600 mt-2 line-clamp-3 leading-relaxed">
              {product.kort_beskrivelse}
            </p>
          )}
        </div>

        {/* Link / CTA to Product Details - purely informational, no cart or buy buttons */}
        <div className="mt-5 pt-4 border-t border-stone-100 flex items-center justify-between">
          <Link
            to={detailUrl}
            className="text-xs font-bold text-[#006368] hover:text-[#004d51] flex items-center gap-1.5 transition-colors"
          >
            <span>{isEn ? 'View specifications' : 'Se specifikationer'}</span>
            <i className="fa-solid fa-arrow-right text-[11px] group-hover:translate-x-1 transition-transform" />
          </Link>
          {product.specifikationer && (
            <span className="text-[11px] text-stone-400 font-medium">
              <i className="fa-solid fa-sliders mr-1" />
              {isEn ? 'Tech specs' : 'Tekniske data'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
